import { RowType } from '@cmps/ng-kit';

import { KpiType } from '../../coaching-insight/insight-widget/model/kpi-type.model';
import { PayloadAction } from '../../store.model';
import { getCardRowsData } from '../functions/get-card-rows-data.function';
import { getHeaderRowData } from '../functions/get-header-row-data.function';
import { getMi2Item } from '../functions/get-mi2-item.function';
import { getMi2TrendDataSet } from '../functions/get-mi2-trend-data-set.function';
import { getOaTrendDataSet } from '../functions/get-oa-trend-data-set.function';
import { getWinningPctCardItem } from '../functions/get-winnipegPct-item.function';
import { getWpTrendDataSet } from '../functions/get-wp-trend-data-set.function';
import { CardItem } from '../models/card-item.model';
import { CoachingTrendData } from '../models/coaching-trend-data.model';
import { OaScoreType } from '../models/oa-score-type.enum';
import { ActionType, ReloadResult } from '../my-coaching-card.action';
import { CoachingCardState } from '../my-coaching-card.state';

export const handleReloadResult = (
    state: CoachingCardState,
    action: PayloadAction<
        ActionType.MY_COACHING_CARD__ROOT__RELOAD_RESULT,
        ReloadResult
    >,
): CoachingCardState => {
    const {
        coachingCard,
        componentAttributes,
        insightSummaryData,
        coachingCardThreshold,
        mi2DataResult,
        wpCoachingCard,
    } = action.payload;

    const kpiTypeMi2 = insightSummaryData?.kpis?.find(
        (e) =>
            e.kpiTypeId === KpiType.MANAGEMENT_IMPROVEMENT_INDEX ||
            e.kpiTypeId === KpiType.FL_MANAGEMENT_IMPROVEMENT_INDEX,
    );

    const kpiTypeFLOA = insightSummaryData?.kpis?.find(
        (e) => e.kpiTypeId === KpiType.FRONT_LINE_OA,
    );
    const kpiTypeWinningPct = insightSummaryData?.kpis?.find(
        (e) =>
            e.kpiTypeId === KpiType.WINNING_PERCENTAGE ||
            e.kpiTypeId === KpiType.FL_WINNING_PERCENTAGE,
    );
    const kpiTypeOa = insightSummaryData?.kpis?.find(
        (e) => e.kpiTypeId === KpiType.OPPORTUNITY_ALIGNMENT,
    );

    if (coachingCard == null) {
        return {
            ...state,
            pending: {
                ...state.pending,
                reload: false,
                hasData: false,
            },
        };
    }

    const cardItems: CardItem[] = [];
    const trendDataMap = new Map<KpiType, CoachingTrendData>();

    const isTypeFLOA = kpiTypeFLOA != null;
    const headerKpiType = isTypeFLOA
        ? kpiTypeFLOA.kpiTypeId
        : KpiType.OPPORTUNITY_ALIGNMENT;

    const headerData = getHeaderRowData(
        coachingCard.startDate,
        coachingCard.endDate,
        headerKpiType,
    );
    const cardRowsData = getCardRowsData(
        coachingCard,
        coachingCardThreshold,
        componentAttributes,
        isTypeFLOA,
    );

    cardItems.push({
        headerData,
        cardRowsData,
        displayOrder: isTypeFLOA
            ? kpiTypeFLOA?.displayOrder
            : kpiTypeOa?.displayOrder,
        type: RowType.OA,
    });

    if (isTypeFLOA) {
        trendDataMap.set(
            KpiType.FRONT_LINE_OA,
            getOaTrendDataSet(coachingCard.oaSummary, [], true),
        );
    } else {
        const index = cardRowsData.findIndex((e) => e.id === OaScoreType.OA);
        const selectedRows =
            index !== -1
                ? cardRowsData[
                      index
                  ].dropdownMenuData?.dropdownMenuValues[1].subMenu
                      .filter((e) => e.selected)
                      .map((e) => e.id)
                : [];
        trendDataMap.set(
            KpiType.OPPORTUNITY_ALIGNMENT,
            getOaTrendDataSet(coachingCard.oaSummary, selectedRows),
        );
    }

    if (kpiTypeMi2 != null) {
        const mi2Data = getMi2Item(mi2DataResult, kpiTypeMi2);

        if (mi2Data != null) {
            cardItems.push({
                ...mi2Data,
                displayOrder: kpiTypeMi2.displayOrder,
            });
            trendDataMap.set(
                kpiTypeMi2.kpiTypeId,
                getMi2TrendDataSet(mi2DataResult?.miScoreDataList, kpiTypeMi2),
            );
        }
    }

    if (kpiTypeWinningPct != null) {
        const wpItem = getWinningPctCardItem(
            wpCoachingCard,
            coachingCardThreshold,
            kpiTypeWinningPct.kpiTypeId,
        );

        if (wpItem != null) {
            cardItems.push({
                ...wpItem,
                displayOrder: kpiTypeWinningPct.displayOrder,
            });
            trendDataMap.set(
                kpiTypeWinningPct.kpiTypeId,
                wpCoachingCard != null
                    ? getWpTrendDataSet(
                          wpCoachingCard,
                          kpiTypeWinningPct.kpiTypeId,
                      )
                    : null,
            );
        }
    }

    cardItems.sort((a, b) => a.displayOrder - b.displayOrder);

    return {
        ...state,
        pending: {
            ...state.pending,
            reload: false,
            hasData: true,
        },
        cardItems,
        oaSummary: coachingCard.oaSummary,
        trendDataMap,
    };
};
